// Extracted from original/src/App.tsx:14803-14942 (the "users" settings card, ADR-08 §2.1): the user rows
// (avatar, username + employee code badge, role line, restriction badge) and the "add user" button. The old
// delete button (:14917-14930) becomes "deactivate" (D-130). The roles list (D-034, new) reuses the same row
// classes; the old app had no roles screen (roles were the three fixed names). Cut: `users` / `warehouses`
// state and the permission guard (:14804-14809, container).
import { Building2, Edit2, Plus, Shield, ShieldPlus, User, UserCheck, UserX } from 'lucide-react';
import { useT } from '../i18n-context.js';
import { SettingsCardFrame } from './Settings.js';

export interface UserRow {
  readonly id: string;
  readonly username: string;
  readonly displayName: string;
  readonly employeeCode?: string | null;
  readonly roleName: string;
  readonly active: boolean;
  /** Restriction badge text, formatted by the container (D-131), e.g. "كافة الصناديق". */
  readonly restriction?: string | null;
  readonly restrictionIsSpecific?: boolean;
}

export interface RoleRow {
  readonly id: string;
  readonly name: string;
  readonly builtin: string | null;
  readonly permissionsCount: number;
  readonly activeUsers: number;
}

export interface UsersCardProps {
  readonly customIcon?: string;
  readonly users: readonly UserRow[];
  readonly roles: readonly RoleRow[];
  readonly currentUserId: string;
  readonly canManage: boolean;
  readonly onEdit: (id: string) => void;
  readonly onDeactivate: (id: string) => void;
  readonly onAdd: () => void;
  readonly onEditRole?: (id: string) => void;
  /** [C28] D-034: new role (holders of manage_roles). */
  readonly onAddRole?: () => void;
  /** [C28] D-130: reactivate an inactive user (access reset stays on the Security card, D-133). */
  readonly onReactivate?: (id: string) => void;
}

export function UsersCard(props: UsersCardProps) {
  const t = useT();
  return (
    <SettingsCardFrame cardKey="users" {...(props.customIcon ? { customIcon: props.customIcon } : {})}>
      <div className="space-y-4">
        <div className="space-y-2" data-testid="users-list">
          {props.users.map((u) => (
            <div
              key={u.id}
              data-testid={`user-row-${u.username}`}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#05070a] rounded-xl border border-gray-100 dark:border-[#30363d] dark:digital-border"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white dark:bg-[#0d1117] rounded-full flex items-center justify-center text-gray-400 dark:text-[#8b949e] border border-gray-100 dark:border-[#30363d]">
                  <User size={20} />
                </div>
                <div>
                  <p className="font-black text-sm dark:text-[#f0f6fc] uppercase tracking-tight dark:digital-glow flex items-center gap-2">
                    <span>{u.username}</span>
                    {u.employeeCode && (
                      <span className="bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-[10px] px-2 py-0.5 rounded-md font-mono font-black border border-blue-200 dark:border-blue-800/50">
                        {u.employeeCode}
                      </span>
                    )}
                    {!u.active && (
                      <span className="bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400 text-[10px] px-2 py-0.5 rounded-md font-black border border-rose-200 dark:border-rose-800/50">
                        {t('users.inactive')}
                      </span>
                    )}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap mt-0.5">
                    <p className="text-[10px] text-gray-400 dark:text-[#8b949e] uppercase tracking-widest font-bold">
                      {u.roleName}
                    </p>
                    {u.restriction && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold flex items-center gap-1 ${
                        !u.restrictionIsSpecific
                          ? "bg-slate-100 dark:bg-[#161b22] text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-[#30363d]"
                          : "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-900/40"
                      }`}>
                        <Building2 size={10} />
                        <span>{u.restriction}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => props.onEdit(u.id)}
                  data-testid={`user-edit-${u.username}`}
                  className="text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 p-2 rounded-lg transition-colors"
                  title={t('users.edit')}
                >
                  <Edit2 size={16} />
                </button>
                {u.id !== props.currentUserId && props.canManage && !u.active && props.onReactivate && (
                  <button
                    onClick={() => props.onReactivate?.(u.id)}
                    data-testid={`user-reactivate-${u.username}`}
                    className="text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 p-2 rounded-lg transition-colors"
                    title={t('users.reactivate')}
                  >
                    <UserCheck size={16} />
                  </button>
                )}
                {u.id !== props.currentUserId && props.canManage && u.active && (
                  <button
                    onClick={() => props.onDeactivate(u.id)}
                    className="text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 p-2 rounded-lg transition-colors"
                    title={t('users.deactivate')}
                  >
                    <UserX size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        {props.canManage && (
          <button
            onClick={props.onAdd}
            data-testid="users-add"
            className="w-full py-3 bg-blue-600 text-white rounded-xl font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2"
          >
            <Plus size={18} />
            {t('users.add_new')}
          </button>
        )}
        {/* Roles (D-034) — same row style */}
        <div className="pt-4 border-t border-gray-100 dark:border-[#30363d] space-y-2" data-testid="roles-list">
          <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
            {t('users.roles_title')}
          </label>
          {props.roles.map((r) => (
            <div
              key={r.id}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#05070a] rounded-xl border border-gray-100 dark:border-[#30363d] dark:digital-border"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white dark:bg-[#0d1117] rounded-full flex items-center justify-center text-gray-400 dark:text-[#8b949e] border border-gray-100 dark:border-[#30363d]">
                  <Shield size={18} />
                </div>
                <div>
                  <p className="font-black text-sm dark:text-[#f0f6fc] tracking-tight flex items-center gap-2">
                    <span>{r.name}</span>
                    {r.builtin && (
                      <span className="bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 text-[10px] px-2 py-0.5 rounded-md font-black border border-amber-200 dark:border-amber-800/50">
                        {t('users.builtin')}
                      </span>
                    )}
                  </p>
                  <p className="text-[10px] text-gray-400 dark:text-[#8b949e] uppercase tracking-widest font-bold">
                    {t('users.permissions_count', { n: r.permissionsCount })} · {t('users.active_users', { n: r.activeUsers })}
                  </p>
                </div>
              </div>
              {props.onEditRole && r.builtin !== 'super_manager' && (
                <button
                  onClick={() => props.onEditRole?.(r.id)}
                  data-testid={`role-edit-${r.id}`}
                  className="text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 p-2 rounded-lg transition-colors"
                  title={t('roles.edit_title')}
                >
                  <Edit2 size={16} />
                </button>
              )}
            </div>
          ))}
          {props.onAddRole && (
            <button
              onClick={props.onAddRole}
              data-testid="roles-add"
              className="w-full py-3 border-2 border-dashed border-blue-200 dark:border-blue-900/50 text-blue-600 dark:text-blue-400 rounded-xl font-black uppercase tracking-widest hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all flex items-center justify-center gap-2"
            >
              <ShieldPlus size={18} />
              {t('roles.add_new')}
            </button>
          )}
        </div>
      </div>
    </SettingsCardFrame>
  );
}
